import React, { useState } from 'react';
import { Button } from 'components/ui/button';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/pro-solid-svg-icons';
import Toggle from '../../toggle';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import LoadingSpinner from 'components/ui/loading_spinner';

type PartnerConnectionModalProps = {
  allowManualSignups: boolean;
  allowNoCredSignups: boolean;
  connected: boolean;
  modalOpen: boolean;
  noCredSignup: boolean;
  partnerName: string;
  sendingRequest: boolean;
  onClose: () => void;
  onConfirm: (credentials?: { username: string; password: string }) => void;
  toggleNoCredRegistration: () => void;
}

export default function PartnerConnectionModal({
  allowManualSignups,
  allowNoCredSignups,
  partnerName,
  onClose,
  onConfirm,
  modalOpen,
  noCredSignup,
  sendingRequest,
  toggleNoCredRegistration,
  connected
}: PartnerConnectionModalProps) {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  const updateCredentials = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const manuallyConnectPartner = (e) => {
    e.preventDefault();
    onConfirm(credentials);
  };

  const closeModal = () => onClose();

  const automaticRegistrationToggle = () => {
    return (
      <Toggle
        checked={noCredSignup}
        disabled={!allowManualSignups}
        showText={false}
        theme="ds"
        onChange={toggleNoCredRegistration}
      />
    );
  };
  const manualSignupText = `Sign in with your ${partnerName} username and password and all syndicated listings will be \
                            sent to ${partnerName}.`;
  const noCredSignUpText = `Click Connect and we will automatically register you with ${partnerName} and start \
                               sending all syndicated listings to them.`;
  const disableManualSignupTooltip = () => {
    const tooltip = <Tooltip id="manual-signup-tooltip">
      {partnerName} does not support registration with credentials.
    </Tooltip>;

    return (
      <OverlayTrigger
        overlay={tooltip}
        placement="top"
      >
        <span>
          {automaticRegistrationToggle()}
        </span>
      </OverlayTrigger>
    );
  };

  const renderConnectForm = () => {
    return (
      <React.Fragment>
        <Modal.Body>
          {allowNoCredSignups && (
            <React.Fragment>
              <div className="d-flex align-items-center justify-content-center mb-4 gap-3">
                {
                  allowManualSignups ? (
                    <Toggle
                      checked={noCredSignup}
                      showText={false}
                      theme="ds"
                      onChange={toggleNoCredRegistration}
                    />
                  ) : disableManualSignupTooltip()
                }
                <div className="text-center">Automatically register without credentials</div>
              </div>
              {allowManualSignups && !noCredSignup && (
                <div className="d-flex align-items-center justify-content-between gap-4 mb-4">
                  <hr className="flex-grow-1 m-0" />
                  <div>OR</div>
                  <hr className="flex-grow-1 m-0" />
                </div>
              )}
            </React.Fragment>
          )}
          {noCredSignup && (
            <React.Fragment>
              <div className="text-center">{noCredSignUpText}</div>
            </React.Fragment>
          )}
          {!noCredSignup && (
            <React.Fragment>
              <div className="text-center">{manualSignupText}</div>
              <form className="d-flex flex-column gap-3" onSubmit={manuallyConnectPartner}>
                <div className="d-flex flex-column gap-1">
                  <label
                    className="form-label"
                    htmlFor="username"
                  >
                    Username
                  </label>
                  <input
                    className="form-control"
                    id="username"
                    name="username"
                    placeholder="Enter your username"
                    required
                    type="text"
                    value={credentials.username}
                    onChange={updateCredentials}
                  />
                </div>

                <div className="d-flex flex-column gap-1">
                  <label
                    className="form-label"
                    htmlFor="password"
                  >
                    Password
                  </label>
                  <input
                    className="form-control"
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    required
                    type="password"
                    value={credentials.password}
                    onChange={updateCredentials}
                  />
                </div>
              </form>
            </React.Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button label="Cancel" variant="ghost" onClick={closeModal} />
          <Button disabled={sendingRequest} onClick={manuallyConnectPartner}>
            {sendingRequest && <LoadingSpinner flex size="md" skipTopBuffer />}
            {sendingRequest ? 'Connecting...' : 'Connect'}
          </Button>
        </Modal.Footer>
      </React.Fragment>
    );
  };

  const renderDisconnectForm = () => {
    return (
      <React.Fragment>
        <Modal.Body>
          <div className="text-center">
            Are you sure you would like to disconnect from {partnerName}?
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button label="Cancel" variant="ghost" onClick={closeModal} />
          <Button disabled={sendingRequest} variant="destructive" onClick={() => onConfirm()}>
            {sendingRequest && <LoadingSpinner flex size="md" skipTopBuffer />}
            {sendingRequest ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </Modal.Footer>
      </React.Fragment>
    );
  };

  return (
    <Modal open={modalOpen} onClose={closeModal}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title className="d-flex gap-2 align-items-center">
            <FontAwesomeIcon className="text-primary" icon={faExchangeAlt} />
            {`${connected ? 'Disconnect from' : 'Connect to'} ${partnerName}`}
          </Modal.Title>
        </Modal.Header>
        {connected ? renderDisconnectForm() : renderConnectForm()}
      </Modal.Content>
    </Modal>
  );
}
